import http from '../../../api/httpClient';

interface AlertFilter {
  discrepancy_type?: 'fastag_vs_sim' | 'distance_increasing' | string;
  untracked?: 'missing_pings' | 'missing_lhc' | string;
  arrival?: 'rd_bucket_t1' | 'rd_bucket_t2' | 'rd_bucket_t3' | string;
}

interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}

interface AlertCard {
  id: string;
  title: string;
  type: 'bucketed' | 'single';
  style: 'positive' | 'negative' | 'neutral' | 'total';
  count?: number;
  filters?: AlertFilter;
  buckets?: AlertBucket[];
}

interface AlertsResponse {
  cards: AlertCard[];
}

interface ShipmentParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  from?: string;
  to?: string;
  discrepancy_type?: string;
  untracked?: string;
  arrival?: string;
  cust_id?: string;
  booking_branch?: string;
  status?: string;
  pod_not_received?: string;
  pending_invoice?: string;
  load_type?: string;
  shipment_status?: string;
}

interface AlertParams {
  threshold_1?: number;
  threshold_2?: number;
  threshold_3?: number;
  search?: string;
  from?: string;
  to?: string;
  cust_id?: string;
  load_type?: string;
}

interface TabsParams {
  cust_id?: string;
  from?: string;
  to?: string;
  search?: string;
  booking_branch?: string;
  arrival?: string;
  untracked?: string;
  discrepancy_type?: string;
}

export interface TabInfo {
  id: string;
  label: string;
  count: number;
}

export interface InTransitTabsResponse {
  tabs: TabInfo[];
}

export interface CustomerOption {
  cust_code: string;
  cust_name: string;
}

export interface BranchOption {
  booking_branch: string;
  branch_name: string;
}

// Local Shipments
export interface LocalShipment {
  cn_number: string;
  cn_date: string;
  ewaybill_numbers: string[];
  invoice_numbers: string[];
  branch_name: string;
  consignor: string;
  consignee: string;
  origin: string;
  destination: string;
}

export const getCustomerCodes = async (
  params: Pick<ShipmentParams, 'from' | 'to' | 'search'> & { status?: string } = {}
): Promise<CustomerOption[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.from) queryParams.append("from", params.from);
    if (params.to) queryParams.append("to", params.to);
    if (params.search && params.search.trim()) queryParams.append("search", params.search.trim());
    if (params.status) {
      queryParams.append("status", params.status);
    }

    const response = await http.get(`/api/v1/cto/shipments/customers?${queryParams.toString()}`);
    return response;
  } catch (error) {
    console.error("Error fetching customer codes:", error);
    throw error;
  }
};

export const getBranches = async (
  params: Pick<ShipmentParams, 'from' | 'to' | 'search'> & { status?: string } = {}
): Promise<BranchOption[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    if (params.search && params.search.trim()) queryParams.append('search', params.search.trim());
    if (params.status) {
      queryParams.append('status', params.status);
    }

    const response = await http.get<BranchOption[]>(`/api/v1/cto/shipments/branches?${queryParams.toString()}`);
    return response;
  } catch (error) {
    console.error('Error fetching branches:', error);
    throw error;
  }
};


export const getInTransitShipments = async (params: ShipmentParams = {}) => {
  try {
    const {
      page = 1,
      per_page = 20,
      search = '',
      sort_by = 'cn_date',
      sort_order = 'desc',
      from,
      to,
      discrepancy_type,
      untracked,
      arrival,
      cust_id,
      booking_branch,
      load_type,
      shipment_status
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
    });

    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }

    if (from) {
      queryParams.append('from', from);
    }

    if (to) {
      queryParams.append('to', to);
    }

    if (discrepancy_type) {
      queryParams.append('discrepancy_type', discrepancy_type);
    }
    if (untracked) {
      queryParams.append('untracked', untracked);
    }
    if (arrival) {
      queryParams.append('arrival', arrival);
    }
    if (cust_id) {
      queryParams.append('cust_id', cust_id);
    }
    if (booking_branch) {
      queryParams.append('booking_branch', booking_branch);
    }
    if (load_type) {
      queryParams.append('load_type', load_type);
    }
    if (shipment_status) {
      queryParams.append('shipment_status', shipment_status);
    }

    const response = await http.get(`/api/v1/cto/shipments/in-transit?${queryParams}`);
    return response;
  } catch (error) {
    console.error('Error fetching in-transit shipments:', error);
    throw error;
  }
};

export const getDeliveredShipments = async (params: ShipmentParams = {}) => {
  try {
    const {
      page = 1,
      per_page = 20,
      search = '',
      sort_by = 'cn_date',
      sort_order = 'desc',
      from,
      to,
      cust_id,
      booking_branch,
      pod_not_received,
      pending_invoice
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order
    });

    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }

    if (from) {
      queryParams.append('from', from);
    }

    if (to) {
      queryParams.append('to', to);
    }

    if (cust_id) {
      queryParams.append('cust_id', cust_id);
    }
    if (booking_branch) {
      queryParams.append('booking_branch', booking_branch);
    }

    if (pod_not_received) {
      queryParams.append('pod_not_received', pod_not_received);
    }
    if (pending_invoice) {
      queryParams.append('pending_invoice', pending_invoice);
    }

    const response = await http.get(`/api/v1/cto/shipments/delivered?${queryParams}`);
    return response;
  } catch (error) {
    console.error('Error fetching delivered shipments:', error);
    throw error;
  }
};

export const getLocalShipments = async (params: ShipmentParams = {}) => {
  try {
    const {
      page = 1,
      per_page = 20,
      search = '',
      sort_by = 'cn_date',
      sort_order = 'desc',
      from,
      to,
      cust_id,
      booking_branch
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order
    });

    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }

    if (from) {
      queryParams.append('from', from);
    }

    if (to) {
      queryParams.append('to', to);
    }

    if (cust_id) {
      queryParams.append('cust_id', cust_id);
    }
    if (booking_branch) {
      queryParams.append('booking_branch', booking_branch);
    }

    const response = await http.get(`/api/v1/cto/shipments/local?${queryParams}`);
    return response;
  } catch (error) {
    console.error('Error fetching local shipments:', error);
    throw error;
  }
};

export const getDMVShipments = async (params: ShipmentParams = {}) => {
  try {
    const {
      page = 1,
      per_page = 20,
      search = '',
      sort_by = 'cn_date',
      sort_order = 'desc',
      from,
      to,
      cust_id,
      booking_branch
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order
    });

    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }

    if (from) {
      queryParams.append('from', from);
    }

    if (to) {
      queryParams.append('to', to);
    }

    if (cust_id) {
      queryParams.append('cust_id', cust_id);
    }
    if (booking_branch) {
      queryParams.append('booking_branch', booking_branch);
    }

    const response = await http.get(`/api/v1/cto/shipments/dmv?${queryParams}`);
    return response;
  } catch (error) {
    console.error('Error fetching local shipments:', error);
    throw error;
  }
};

export const getInTransitAlerts = async (params: AlertParams = {}): Promise<AlertsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search && params.search.trim()) {
      queryParams.append('search', params.search.trim());
    }
    if (params.from) {
      queryParams.append('from', params.from);
    }
    if (params.to) {
      queryParams.append('to', params.to);
    }
    if (params.cust_id) {
      queryParams.append('cust_id', params.cust_id);
    }
    if (params.load_type) {
      queryParams.append('load_type', params.load_type);
    }

    if (params.threshold_1 !== undefined) {
      queryParams.append('threshold_1', params.threshold_1.toString());
    }
    if (params.threshold_2 !== undefined) {
      queryParams.append('threshold_2', params.threshold_2.toString());
    }
    if (params.threshold_3 !== undefined) {
      queryParams.append('threshold_3', params.threshold_3.toString());
    }

    const response = await http.get<AlertsResponse>(`/api/v1/cto/shipments/in-transit/alerts?${queryParams}`);
    return response;
  } catch (error) {
    console.error('Error fetching in-transit alerts:', error);
    throw error;
  }
};

export const getDeliveredAlerts = async (params: AlertParams = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search && params.search.trim()) {
      queryParams.append('search', params.search.trim());
    }
    if (params.from) {
      queryParams.append('from', params.from);
    }
    if (params.to) {
      queryParams.append('to', params.to);
    }
    if (params.cust_id) {
      queryParams.append('cust_id', params.cust_id);
    }

    const response = await http.get(
      `/api/v1/cto/shipments/delivered/alerts?${queryParams}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching delivered alerts:', error);
    throw error;
  }
};

export const getTrackingData = async (cnNumber: string) => {
  try {
    const response = await http.get(`/api/v1/cto/track?cn_number=${encodeURIComponent(cnNumber)}`);
    return response;
  } catch (error) {
    console.error('Error fetching tracking data:', error);
    throw error;
  }
};

export interface TrackingMapLocation {
  address: string;
  lat: number;
  lon: number;
}

export interface TrackingMapMarker {
  id: number;
  timestamp: string;
  lat: number;
  lon: number;
  address: string;
}

export interface TrackingMapData {
  success: boolean;
  cn_number: string;
  origin: TrackingMapLocation;
  destination: TrackingMapLocation;
  markers: {
    sim: TrackingMapMarker[];
    fastag: TrackingMapMarker[];
    manual: TrackingMapMarker[];
    incident: TrackingMapMarker[];
  };
  error_message: string | null;
}

export const getTrackingMapData = async (cnNumber: string): Promise<TrackingMapData> => {
  try {
    const response = await http.get<TrackingMapData>(`/api/v1/cto/track/map?cn_number=${encodeURIComponent(cnNumber)}`);
    return response;
  } catch (error) {
    console.error('Error fetching tracking map data:', error);
    throw error;
  }
};

export interface LhcCn {
  cn_number: string;
  cn_date: string;
  branch_name: string;
  consignor: string;
  consignee: string;
  origin: string;
  destination: string;
  edd: string;
  remaining_km: number;
}

export interface LhcData {
  lhc_number: string;
  lhc_date: string;
  lhc_origin: string;
  lhc_destination: string;
  vehicle_no: string;
  cns: LhcCn[];
}

export interface DeliveryDateUpdateResult {
  cn_number: string;
  success: boolean;
  message: string;
}

export interface DeliveryDateUpdateResponse {
  results: DeliveryDateUpdateResult[];
  total: number;
  successful: number;
  failed: number;
}

export const getLastLhc = async (cnNumber: string): Promise<LhcData> => {
  try {
    const response = await http.get<LhcData>(`/api/v1/cto/shipments/in-transit/${encodeURIComponent(cnNumber)}/details`);
    return response;
  } catch (error) {
    console.error('Error fetching LHC data:', error);
    throw error;
  }
};

export interface LhcItem {
  lhc_number: string;
  lhc_date: string;
  lhc_distance_km?: number | null;
  origin?: string | null;
  destination?: string | null;
  vehicle_no?: string | null;
  vehicle_type?: string | null;
  driver_name?: string | null;
  driver_mobile?: string | null;
  branch_name?: string | null;
}

export interface CnLinkedLhcsResponse {
  lhcs: LhcItem[];
}

export const getTripDetails = async (cnNumber: string) => {
  try {
    const response = await http.get(`/api/v1/cto/shipments/in-transit/${encodeURIComponent(cnNumber)}/lhcs`);
    return response;
  } catch (error) {
    console.error("Error fetching all Lhc Details: ", error);
    throw error;
  }
}

export const updateDeliveryDates = async (deliveryDate: string, cnNumbers: string[]): Promise<DeliveryDateUpdateResponse> => {
  try {
    const body = {
      delivery_date: deliveryDate,
      cn_numbers: cnNumbers
    };
    const response = await http.patch<DeliveryDateUpdateResponse>('/api/v1/cto/shipments/delivery-dates', body);
    return response;
  } catch (error) {
    console.error('Error updating delivery dates:', error);
    throw error;
  }
};

export interface GeocodeResponse {
  success: boolean;
  lat?: number;
  lon?: number;
  formatted_address?: string;
  locality?: string;
  city?: string;
  state?: string;
  country_code?: string;
  error_message?: string | null;
  message?: string;
}

export interface ManualPingRequest {
  cn_number: string;
  lat: number;
  lon: number;
  formatted_address: string;
  locality: string;
  city: string;
  state: string;
  country_code: string;
  [key: string]: unknown;
}

export interface ManualPingResponse {
  success: boolean;
  message: string;
}

export const geocodeLocation = async (query: string): Promise<GeocodeResponse> => {
  try {
    const body = { query };
    const response = await http.post<GeocodeResponse>('/api/v1/cto/track/geocode/preview', body);
    return response;
  } catch (error) {
    console.error('Error geocoding location:', error);
    throw error;
  }
};

export const saveManualPing = async (data: ManualPingRequest): Promise<ManualPingResponse> => {
  try {
    const response = await http.post<ManualPingResponse>('/api/v1/cto/track/manual-ping', data);
    return response;
  } catch (error) {
    console.error('Error saving manual ping:', error);
    throw error;
  }
};

export interface DeletePingResponse {
  success: boolean;
  message: string;
}

export interface RestorePingResponse {
  success: boolean;
  message: string;
}

export const deletePing = async (pingId: number, cnNumber: string): Promise<DeletePingResponse> => {
  try {
    const response = await http.delete<DeletePingResponse>(`/api/v1/cto/track/ping/${pingId}?cn_number=${encodeURIComponent(cnNumber)}`);
    return response;
  } catch (error) {
    console.error('Error deleting ping:', error);
    throw error;
  }
};

export const restorePing = async (pingId: number, cnNumber: string): Promise<RestorePingResponse> => {
  try {
    const response = await http.post<RestorePingResponse>(`/api/v1/cto/track/ping/${pingId}/restore?cn_number=${encodeURIComponent(cnNumber)}`);
    return response;
  } catch (error) {
    console.error('Error restoring ping:', error);
    throw error;
  }
};

// Geocoding Errors API
export interface GeocodingErrorsCountResponse {
  count: number;
}

export interface BadPincode {
  pincode: string;
  addresses: string[];
}

export interface GeocodingErrorsResponse {
  bad_pincodes: BadPincode[];
}

export interface GeolocationPreviewRequest {
  pincode: string;
  location_text: string;
  [key: string]: unknown;
}

export interface GeolocationPreviewResponse {
  success: boolean;
  pincode: string | null;
  place_id: string | null;
  lat: number | null;
  lon: number | null;
  formatted_address: string | null;
  sublocality: string | null;
  locality: string | null;
  admin_area_level_3: string | null;
  admin_area_level_2: string | null;
  admin_area_level_1: string | null;
  admin_area1_code: string | null;
  country: string | null;
  country_code: string | null;
  error_message: string | null;
}

export interface GeolocationSaveRequest {
  pincode: string;
  place_id: string;
  lat: number;
  lon: number;
  formatted_address: string;
  sublocality: string | null;
  locality: string | null;
  admin_area_level_3: string | null;
  admin_area_level_2: string | null;
  admin_area_level_1: string | null;
  admin_area1_code: string | null;
  country: string;
  country_code: string;
  [key: string]: unknown;
}

export interface GeolocationSaveResponse {
  success: boolean;
  message: string;
}

export const getGeocodingErrorsCount = async (): Promise<GeocodingErrorsCountResponse> => {
  try {
    const response = await http.get<GeocodingErrorsCountResponse>('/api/v1/cto/geolocation/bad-pincodes/count');
    return response;
  } catch (error) {
    console.error('Error fetching geocoding errors count:', error);
    throw error;
  }
};

export const getGeocodingErrors = async (): Promise<GeocodingErrorsResponse> => {
  try {
    const response = await http.get<GeocodingErrorsResponse>('/api/v1/cto/geolocation/bad-pincodes');
    return response;
  } catch (error) {
    console.error('Error fetching geocoding errors:', error);
    throw error;
  }
};

export const previewGeolocation = async (data: GeolocationPreviewRequest): Promise<GeolocationPreviewResponse> => {
  try {
    const response = await http.post<GeolocationPreviewResponse>('/api/v1/cto/geolocation/preview', data);
    return response;
  } catch (error) {
    console.error('Error previewing geolocation:', error);
    throw error;
  }
};

export const saveGeolocation = async (data: GeolocationSaveRequest): Promise<GeolocationSaveResponse> => {
  try {
    const response = await http.post<GeolocationSaveResponse>('/api/v1/cto/geolocation/save', data);
    return response;
  } catch (error) {
    console.error('Error saving geolocation:', error);
    throw error;
  }
};

export const getCnGuid = async (cnNumber: string): Promise<{ cn_guid: string }> => {
  try {
    const response = await http.get(`/api/v1/cto/shipments/get-cn-guid?cn_number=${encodeURIComponent(cnNumber)}`);
    return response;
  } catch (error) {
    console.error("Error fetching cn_guid:", error);
    throw error;
  }
};

export const getInTransitTabs = async (params: TabsParams = {}): Promise<InTransitTabsResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.cust_id) {
      queryParams.append('cust_id', params.cust_id);
    }
    if (params.from) {
      queryParams.append('from', params.from);
    }
    if (params.to) {
      queryParams.append('to', params.to);
    }
    if (params.search && params.search.trim()) {
      queryParams.append('search', params.search.trim());
    }
    if (params.booking_branch) {
      queryParams.append('booking_branch', params.booking_branch);
    }
    if (params.arrival) {
      queryParams.append('arrival', params.arrival);
    }
    if (params.untracked) {
      queryParams.append('untracked', params.untracked);
    }
    if (params.discrepancy_type) {
      queryParams.append('discrepancy_type', params.discrepancy_type);
    }

    const response = await http.get<InTransitTabsResponse>(`/api/v1/cto/shipments/in-transit/tabs?${queryParams}`);
    return response;
  } catch (error) {
    console.error('Error fetching in-transit tabs:', error);
    throw error;
  }
};


export interface ValidityDetail {
  validity_type?: string;
  date?: string;
  details?: string;
}

export interface VehicleDetails {
  vehicle_number: string;
  chasis_number?: string;
  engine_number?: string;
  owner_name?: string;
  manufactured_date?: string;
  registration_date?: string;
  registration_at?: string;
  registration_status?: string;
  make?: string;
  model?: string;
  body_type?: string;
  category?: string;
  category_description?: string;
  fuel_type?: string;
  norms?: string;
  is_financed?: boolean;
  financier_name?: string;
  unladen_weight?: number;
  gross_weight?: number;
  loading_capacity?: number;
  engine_cylinders?: number;
  engine_cubic_capacity?: number;
  seating_capacity?: number;
  sleeping_capacity?: number;
  standing_capacity?: number;
  wheel_base?: number;
  blacklist_status?: string;
  noc_details?: string;
  validity_details: ValidityDetail[];
}

export const getVehicleDetails = async (
  vehicleNo: string
): Promise<VehicleDetails> => {
  try {
    const response = await http.get<VehicleDetails>(`/api/v1/cto/shipments/vehicles/${encodeURIComponent(vehicleNo)}`);
    return response;
  } catch (error) {
    console.error("Error fetching vehicle details:", error);
    throw error;
  }
};